import { forwardRef } from 'react';
import { MapPin, Phone, Globe, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/base/Card';
import { Badge } from '@/components/base/Badge';
import { buildGoogleMapsUrl, buildPhoneUrl, ensureHttps } from '@/lib/url-utils';
import type { ServiceData } from '@/types/services';

/**
 * Content labels for the ServiceCard component.
 * All display text is passed via props to enable CMS integration.
 */
export interface ServiceCardContent {
  /** Label for address field (screen reader text) */
  addressLabel: string;
  /** Label for phone field (screen reader text) */
  phoneLabel: string;
  /** Label for website field (screen reader text) */
  websiteLabel: string;
  /** Golden Buckeye badge label */
  goldenBuckeyeLabel: string;
}

/**
 * Props for the ServiceCard component.
 */
export interface ServiceCardProps {
  /** Content labels for the card */
  content: ServiceCardContent;
  /** Service data to display */
  service: ServiceData;
  /** Display index (1-based) for numbering */
  index: number;
  /** Whether this card is selected */
  isSelected: boolean;
  /** Click handler for the card */
  onClick: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ServiceCard - Displays individual service details in the service directory
 *
 * Shows service name, category/subcategory tags, contact information (address,
 * phone, website), and optional Golden Buckeye badge. Cards can be selected
 * to highlight the corresponding map marker.
 *
 * @example
 * ```tsx
 * <ServiceCard
 *   content={{
 *     addressLabel: 'Address',
 *     phoneLabel: 'Phone',
 *     websiteLabel: 'Website',
 *     goldenBuckeyeLabel: 'Golden Buckeye',
 *   }}
 *   service={serviceData}
 *   index={1}
 *   isSelected={false}
 *   onClick={() => handleSelect(serviceData.id)}
 * />
 * ```
 */
export const ServiceCard = forwardRef<HTMLDivElement, ServiceCardProps>(
  ({ content, service, index, isSelected, onClick, className }, ref) => {
    // Build all tags from category and subcategories
    const tags = [service.category, ...service.subcategories];

    // Build full address for display and Google Maps link
    // Only show address if there's a street address (not just state/city)
    const hasStreetAddress = Boolean(service.address);
    const fullAddress = hasStreetAddress
      ? [service.address, service.city, service.state, service.zipCode]
          .filter(Boolean)
          .join(', ')
      : null;

    // Handle card click - only trigger if the click wasn't on a link
    const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
      // Check if the click target is a link or inside a link
      const target = e.target as HTMLElement;
      if (target.closest('a')) {
        return; // Don't trigger onClick if clicking a link
      }
      onClick();
    };

    // Handle keyboard activation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        // Don't activate if focus is on a link
        const activeElement = document.activeElement;
        if (activeElement?.tagName === 'A') {
          return;
        }
        e.preventDefault();
        onClick();
      }
    };

    return (
      <Card
        ref={ref}
        tabIndex={0}
        aria-label={`${service.name}. ${isSelected ? 'Selected.' : ''} Click to ${isSelected ? 'deselect' : 'select'}.`}
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
        className={cn(
          'cursor-pointer transition-all p-4',
          'hover:bg-muted/50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          isSelected && 'ring-2 ring-primary',
          className
        )}
        data-selected={isSelected}
      >
        {/* Service Name with Index */}
        <h3 className="text-lg font-semibold text-foreground mb-3">
          {index}. {service.name}
        </h3>

        {/* Category Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tags.map((tag, tagIndex) => (
              <Badge key={tagIndex} variant="default" size="sm">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Contact Information */}
        <div className="space-y-2">
          {/* Address */}
          {fullAddress && (
            <div>
              <a
                href={buildGoogleMapsUrl(fullAddress)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors group"
                aria-label={`${content.addressLabel}: ${fullAddress}. Opens in new tab`}
              >
                <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                <span className="truncate">{fullAddress}</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground group-hover:text-primary flex-shrink-0" aria-hidden="true" />
              </a>
            </div>
          )}

          {/* Phone */}
          {service.phone && (
            <div>
              <a
                href={buildPhoneUrl(service.phone)}
                className="inline-flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors group"
                aria-label={`${content.phoneLabel}: ${service.phone}`}
              >
                <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                <span>{service.phone}</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground group-hover:text-primary flex-shrink-0" aria-hidden="true" />
              </a>
            </div>
          )}

          {/* Website */}
          {service.website && (
            <div>
              <a
                href={ensureHttps(service.website)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors group"
                aria-label={`${content.websiteLabel}: ${service.website}. Opens in new tab`}
              >
                <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                <span className="truncate">{service.website}</span>
                <ExternalLink className="h-3 w-3 text-muted-foreground group-hover:text-primary flex-shrink-0" aria-hidden="true" />
              </a>
            </div>
          )}
        </div>

        {/* Golden Buckeye Badge */}
        {service.isGoldenBuckeye && (
          <div className="mt-4 flex justify-end">
            <Badge variant="secondary" size="sm" icon="Award" iconColor="yellow">
              {content.goldenBuckeyeLabel}
            </Badge>
          </div>
        )}
      </Card>
    );
  }
);

ServiceCard.displayName = 'ServiceCard';
